/**
 * Shared attach-file constants and helpers.
 *
 * Extracted from AttachFileIcon so multiple components (icons, thumbnails) reuse the same
 * file-type color/label mapping instead of duplicating it.
 */

const LIST_COLOR = {
    grey: '#778899',
    black: '#000000',
    pink: '#ff6b81',
    red: '#ff0000',
    blue: '#0000cd',
    green: '#32cd32',
    orange: '#ff4500',
    violet: '#800080',
    brown: '#cd853f',
    redBrick: '#9f0000'
};

export const LIST_SUPPORT_ATTACH_FILE = {
    EXT_FILE_PAGE_KNOWLEDGE: {
        DISPLAY: 'page',
        LIST: [],
        COLOR_DISPLAY: LIST_COLOR.redBrick
    },
    EXT_FILE_OTHER: {
        DISPLAY: 'others',
        LIST: ['others'],
        COLOR_DISPLAY: LIST_COLOR.black
    },
    EXT_FILE_TXT: {
        DISPLAY: 'txt',
        LIST: ['txt'],
        COLOR_DISPLAY: LIST_COLOR.grey
    },
    EXT_FILE_ZIP: {
        DISPLAY: 'zip',
        LIST: ['zip'],
        COLOR_DISPLAY: LIST_COLOR.brown
    },
    EXT_FILE_PDF: {
        DISPLAY: 'pdf',
        LIST: ['pdf'],
        COLOR_DISPLAY: LIST_COLOR.red
    },
    EXT_FILE_HTML: {
        DISPLAY: 'html',
        LIST: ['html', 'htm'],
        COLOR_DISPLAY: LIST_COLOR.black
    },
    EXT_FILE_IMAGE: {
        DISPLAY: 'image',
        LIST: ['jpg', 'jpeg', 'gif', 'png', 'bmp', 'tif', 'tiff', 'webp'],
        COLOR_DISPLAY: LIST_COLOR.pink
    },
    EXT_FILE_DOC: {
        DISPLAY: 'doc',
        LIST: ['doc', 'docx', 'docm'],
        COLOR_DISPLAY: LIST_COLOR.blue
    },
    EXT_FILE_XLS: {
        DISPLAY: 'xls',
        LIST: ['xls', 'xlsx', 'xlsm', 'csv'],
        COLOR_DISPLAY: LIST_COLOR.green
    },
    EXT_FILE_PPT: {
        DISPLAY: 'ppt',
        LIST: ['ppt', 'pptx', 'pptm'],
        COLOR_DISPLAY: LIST_COLOR.orange
    },
    EXT_FILE_VIDEO: {
        DISPLAY: 'video',
        LIST: ['mov', 'avi', 'mpeg', 'mp4', 'wmv', 'flv'],
        COLOR_DISPLAY: LIST_COLOR.violet
    },
    EXT_FILE_AUDIO: {
        DISPLAY: 'audio',
        LIST: ['wav', 'm4a', 'm4p', 'dsf', 'flac', 'mqa', 'mp3'],
        COLOR_DISPLAY: LIST_COLOR.black
    },
    EXT_FILE_AUTOCAD: {
        DISPLAY: 'AutoCAD',
        LIST: ['dxf', 'dwg', 'igs'],
        COLOR_DISPLAY: LIST_COLOR.black
    }
};

/**
 * Get file type info from extension.
 * @param {string} extension - File extension (e.g., 'pdf', 'jpg', 'docx'); dot optional.
 * @returns {{ DISPLAY: string, COLOR_DISPLAY: string, LIST: string[] }}
 */
export const getFileExtInfo = extension => {
    if (!extension) {
        return LIST_SUPPORT_ATTACH_FILE.EXT_FILE_OTHER;
    }

    const ext = extension.toLowerCase().replace('.', '');

    const found = Object.values(LIST_SUPPORT_ATTACH_FILE).find(
        item => item.LIST && item.LIST.includes(ext)
    );

    return found || LIST_SUPPORT_ATTACH_FILE.EXT_FILE_OTHER;
};

/**
 * Extract the file extension (without the dot) from a filename. Returns '' when there is
 * no real extension (dot-less name), so callers fall back to the "others" type.
 * @param {string} fileName
 * @returns {string}
 */
export const getExtFromFileName = fileName => {
    if (!fileName) return '';
    const dot = fileName.lastIndexOf('.');
    if (dot <= 0 || dot === fileName.length - 1) return '';
    return fileName.slice(dot + 1).toLowerCase();
};

export const THUMBNAIL_SIZE_DEFAULT = { PC: 'md', MOBILE: 'sm'};
export const THUMBNAIL_SIZE_CLIENT_CONTROL = {
    TICKET_LIST: {
        PC: 'lg',
        MOBILE: 'xs'
    },
    TICKET_LIST_MODAL: {
        PC: 'xl',
        MOBILE: 'sm'
    },
    TICKET_DETAIL: {
        PC: 'xl',
        MOBILE: 'md'
    }
};


/**
 * Resolve a `size` prop (a preset name 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', or a raw
 * numeric height) to concrete { width, height } that keep the 56:40 aspect ratio. Shared
 * by AttachFileThumbnailIcon (SVG) and AttachFileThumbnail (image preview) so both size
 * the same way.
 * @param {('xs'|'sm'|'md'|'lg'|'xl'|'xxl'|number)} [size='md']
 * @returns {{ width: number, height: number }}
*/
export const resolveAttachIconSize = (size = THUMBNAIL_SIZE_DEFAULT.PC) => {
    // SVG aspect ratio of the attach icon (viewBox 0 0 56 40).
    const ATTACH_ICON_RATIO = 56 / 40;
    
    // Named height presets for the attach thumbnail (icon + image). Each preset fixes the
    // HEIGHT; the width is derived from ATTACH_ICON_RATIO so the thumbnail never distorts.
    const ATTACH_ICON_SIZE = {
        xs: 40,
        sm: 50,
        md: 60,
        lg: 72,
        xl: 88,
        xxl: 104
    };

    const height = typeof size === 'number' ? size : ATTACH_ICON_SIZE[size] || ATTACH_ICON_SIZE.md;
    return { width: Math.round(height * ATTACH_ICON_RATIO), height };
};

// Single source for the badge/file-name font so the literal is never duplicated.
const BADGE_FONT_FAMILY = "'Zen Kaku Gothic New', sans-serif";

/**
 * Geometry of the file-type badge shown on both the file-icon card and the image preview.
 * The badge is a 24x11-unit shape (inside the icon's 56x40 viewBox) with a 5-unit
 * bottom-right corner radius. The label is anchored at the shape's center.
 */
export const ATTACH_BADGE = {
    VIEWBOX_WIDTH: 24,
    VIEWBOX_HEIGHT: 11,
    // SVG path of the badge shape (square top-left, rounded bottom-right). Shared so the
    // file-icon badge and the image-preview badge draw the exact same shape.
    PATH: 'M0 0H24V6C24 8.76142 21.7614 11 19 11H0V0Z',
    // Font family for the badge label and the file-name text.
    FONT_FAMILY: BADGE_FONT_FAMILY,
    // Fixed <text> props for the badge label, shared by both badges so they render
    // identically. Spread these onto the <text> and add only the varying `fontSize`.
    // x/y = center of the 24x11 shape; middle anchors keep the label dead-center.
    TEXT_PROPS: {
        x: 12,
        y: 5.5,
        fill: '#faf7f7',
        fontWeight: '500',
        textAnchor: 'middle',
        dominantBaseline: 'middle',
        fontFamily: BADGE_FONT_FAMILY
    }
};

// Badge drawing tunables. Named so the geometry in resolveAttachBadgeMetrics stays readable
// and can be adjusted in one place.
const BADGE_METRICS = {
    // Overall size multiplier for the whole badge shape (icon transform + image pixel size).
    SIZE_FACTOR: 1.25,
    // Trim applied to the label size so the text sits a bit smaller inside the badge.
    FONT_FACTOR: 0.9,
    // Base-scale clamp: shrinks the badge as the card grows (50/height), capped so it is
    // neither too big on small cards (MAX) nor too small on large cards (MIN).
    SCALE_MIN: 0.6,
    SCALE_MAX: 0.8,
    // Card height (px) at/below which the badge would be full size before the cap applies.
    SCALE_REFERENCE_HEIGHT: 50,
    // Label font size (viewBox units) by text length, so long extensions still fit.
    FONT_SIZE_SHORT: 10, // length <= SHORT_MAX_LEN
    FONT_SIZE_MEDIUM: 8, // length <= MEDIUM_MAX_LEN
    FONT_SIZE_LONG: 6.5, // longer labels
    SHORT_MAX_LEN: 4,
    MEDIUM_MAX_LEN: 6,
    // viewBox height of the icon the badge lives in (unit = height/VIEWBOX_REFERENCE_HEIGHT).
    VIEWBOX_REFERENCE_HEIGHT: 40
};

/**
 * Resolve the badge's drawing metrics for a given thumbnail size and label.
 *
 * Shared by AttachFileThumbnailIcon (badge drawn inside the 56x40 icon viewBox) and
 * AttachFileThumbnail (badge drawn as its own 24x11 SVG over the image preview) so both
 * stay visually identical. Returns:
 * - `scale`: shrinks the badge on larger cards so it never looks oversized (1 up to 50px
 *   tall, then 50/height down to a 0.6 floor). The icon viewBox badge applies this via an
 *   SVG transform; the image badge folds it into its pixel width/height.
 * - `fontSize`: label font size in viewBox units, chosen by text length so long extensions
 *   still fit inside the badge.
 * - `width` / `height`: badge size in pixels (already scaled). Used by the image preview,
 *   whose badge is a standalone SVG sized in CSS pixels. 1 viewBox unit == height/40 px.
 *
 * @param {('xs'|'sm'|'md'|'lg'|'xl'|'xxl'|number)} [size='md'] - Thumbnail size preset or raw height.
 * @param {string} [text=''] - Badge label (its length drives the font size).
 * @returns {{ scale: number, fontSize: number, width: number, height: number }}
 */
export const resolveAttachBadgeMetrics = (size = THUMBNAIL_SIZE_DEFAULT.PC, text = '') => {
    const { height } = resolveAttachIconSize(size);
    // Base scale shrinks as the card grows (reference/height) so the badge never looks
    // oversized, clamped between SCALE_MIN (large cards) and SCALE_MAX (small cards). The cap
    // keeps the badge from getting too big on small cards (xs/sm/md). SIZE_FACTOR then scales
    // the badge SHAPE (both the file-icon transform and the image badge's pixel size); the
    // label is in viewBox units so it grows with the shape automatically and is NOT multiplied
    // again here.
    const baseScale = Math.min(
        BADGE_METRICS.SCALE_MAX,
        Math.max(BADGE_METRICS.SCALE_MIN, BADGE_METRICS.SCALE_REFERENCE_HEIGHT / height)
    );
    const scale = baseScale * BADGE_METRICS.SIZE_FACTOR;
    // Label size in viewBox units (by text length), trimmed by FONT_FACTOR so the text sits a
    // bit smaller inside the badge.
    const baseFontSize =
        text.length <= BADGE_METRICS.SHORT_MAX_LEN
            ? BADGE_METRICS.FONT_SIZE_SHORT
            : text.length <= BADGE_METRICS.MEDIUM_MAX_LEN
            ? BADGE_METRICS.FONT_SIZE_MEDIUM
            : BADGE_METRICS.FONT_SIZE_LONG;
    const fontSize = baseFontSize * BADGE_METRICS.FONT_FACTOR;
    const unit = (height / BADGE_METRICS.VIEWBOX_REFERENCE_HEIGHT) * scale;
    return {
        scale,
        fontSize,
        width: Math.round(ATTACH_BADGE.VIEWBOX_WIDTH * unit),
        height: Math.round(ATTACH_BADGE.VIEWBOX_HEIGHT * unit)
    };
};
